import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';

const Services = () => {
    const services = [
        {
            title: "Crop Protection Services",
            description: "We provide comprehensive assessment and intervention plans to tackle pest infestations and diseases. Our experts visit your farm to diagnose issues and recommend precise biological or chemical treatments.",
            image: require('../../assets/images/services/crop_protection.png'),
            path: '/services/crop-protection',
            buttonText: "Protect Your Crops"
        },
        {
            title: "Expert Consultancy",
            description: "Get access to top agronomists who analyze your specific conditions—soil type, climate, and resources—to create a tailored farming roadmap for the season, ensuring maximum return on investment.",
            image: require('../../assets/images/services/expert_consultancy.png'),
            path: '/services/expert-consultancy',
            buttonText: "Get Expert Advice"
        },
        {
            title: "Precision Agriculture",
            description: "Leverage satellite imagery, drone mapping, and sensor data to monitor crop health with centimeter-level accuracy. optimize inputs like water and fertilizer where they are needed most.",
            image: require('../../assets/images/services/precision_agriculture.png'),
            path: '/services/precision-agriculture',
            buttonText: "Explore Tech"
        },
        {
            title: "Farm Management Services",
            description: "End-to-end management solutions for large farm holdings. We handle operations, labor management, supply chain logistics, and market linkage so you can focus on ownership.",
            image: require('../../assets/images/services/farm_management.png'),
            path: '/services/farm-management',
            buttonText: "Manage Farm"
        },
        {
            title: "Hydroponics Services",
            description: "Complete setup and maintenance of hydroponic units. From designing the system structure to formulating the perfect nutrient mix, we help you grow soil-less.",
            image: require('../../assets/images/services/hydroponics.png'),
            path: '/services/hydroponics',
            buttonText: "Go Soil-less"
        }
    ];

    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 to-blue-900/80 z-10"></div>
                    <img
                        src="https://images.unsplash.com/photo-1625246333195-bf466a01494e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1932&q=80"
                        alt="Services Hero"
                        className="w-full h-full object-cover opacity-50"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="block text-accent font-bold tracking-widest uppercase text-sm md:text-base mb-4"
                    >
                        What We Do
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-4xl md:text-7xl font-thin mb-6"
                    >
                        Our <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">Services</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Comprehensive agricultural solutions powered by innovation and expertise.
                    </motion.p>
                </div>
            </section>

            {/* Services List */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    className="flex flex-col gap-16"
                >
                    {services.map((service, index) => (
                        <motion.div
                            key={index}
                            variants={fadeInUp}
                            className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 group`}
                        >
                            <div className="lg:w-1/2 relative overflow-hidden min-h-[300px]">
                                <img
                                    src={service.image}
                                    alt={service.title}
                                    className="absolute inset-0 w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </div>

                            <div className="lg:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">
                                    {service.title}
                                </h3>
                                <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                                    {service.description}
                                </p>
                                <Link
                                    to={service.path}
                                    className="inline-flex items-center gap-2 text-primary font-bold uppercase tracking-wider text-sm hover:gap-4 transition-all"
                                >
                                    {service.buttonText}
                                    <ArrowLongRightIcon className="h-5 w-5" />
                                </Link>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </section>
        </div>
    );
};

export default Services;
